import React, { useCallback, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faFileExcel, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import "./PanelInformeUIF.css";
import { getToken } from "../../utils/auth";

const FILAS_POR_PAGINA = 15;

const PanelInformeUIF = ({ darkMode = false }) => {
    const token = getToken();

    const [fechaDesde, setFechaDesde] = useState("2025-03-01");
    const [fechaHasta, setFechaHasta] = useState("2026-03-31");
    const [resumen, setResumen] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [exportando, setExportando] = useState(false);
    const [paginaAltas, setPaginaAltas] = useState(1);
    const [paginaBajas, setPaginaBajas] = useState(1);

    const cardClass = darkMode
        ? "card bg-dark text-white border-secondary informe-uif-card"
        : "card bg-white informe-uif-card";
    const tableVariant = darkMode ? "dark" : undefined;

    const generar = useCallback(async () => {
        if (!token) return;
        setCargando(true);
        setPaginaAltas(1);
        setPaginaBajas(1);
        setResumen(null);
        try {
            const response = await fetch(process.env.REACT_APP_API_INFORME_UIF_RESUMEN, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token, FechaDesde: fechaDesde, FechaHasta: fechaHasta }),
            });
            if (response.status === 401 || response.status === 403) {
                throw new Error("Su sesion ya no es valida.");
            }
            if (!response.ok) throw new Error(`Error ${response.status}`);
            const data = await response.json();
            setResumen(data);
        } catch (err) {
            Swal.fire("Error", err.message || "No se pudo generar el informe.", "error");
        } finally {
            setCargando(false);
        }
    }, [token, fechaDesde, fechaHasta]);

    const exportarExcel = useCallback(async () => {
        if (!token) return;
        setExportando(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_INFORME_UIF_EXCEL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token, FechaDesde: fechaDesde, FechaHasta: fechaHasta }),
            });
            if (!response.ok) throw new Error(`Error ${response.status}`);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `informe_uif_${fechaDesde}_${fechaHasta}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            Swal.fire("Error", err.message || "No se pudo exportar el Excel.", "error");
        } finally {
            setExportando(false);
        }
    }, [token, fechaDesde, fechaHasta]);

    const paginados = (lista, pagina) => {
        const inicio = (pagina - 1) * FILAS_POR_PAGINA;
        return lista.slice(inicio, inicio + FILAS_POR_PAGINA);
    };

    const totalPaginas = (lista) => Math.ceil(lista.length / FILAS_POR_PAGINA);

    const formatFecha = (f) =>
        f ? new Date(f).toLocaleDateString("es-AR") : "—";

    return (
        <div className="container-fluid py-3 px-3 px-md-4">

            {/* Filtros */}
            <Card className={cardClass}>
                <Card.Body>
                    <p className="informe-uif-section-title mb-3">Parámetros del informe</p>
                    <Row className="g-3 align-items-end">
                        <Col xs={12} md={4}>
                            <Form.Label className="lato-bold fs-14">Desde</Form.Label>
                            <Form.Control
                                type="date"
                                value={fechaDesde}
                                onChange={(e) => setFechaDesde(e.target.value)}
                                className={darkMode ? "bg-dark text-white border-secondary" : ""}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="lato-bold fs-14">Hasta</Form.Label>
                            <Form.Control
                                type="date"
                                value={fechaHasta}
                                onChange={(e) => setFechaHasta(e.target.value)}
                                className={darkMode ? "bg-dark text-white border-secondary" : ""}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <Button
                                variant="primary"
                                className="w-100 lato-bold"
                                onClick={generar}
                                disabled={cargando}
                            >
                                {cargando
                                    ? <Spinner size="sm" animation="border" />
                                    : <><FontAwesomeIcon icon={faMagnifyingGlass} className="me-2" />Generar informe</>
                                }
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Resumen */}
            {resumen && (
                <>
                    {/* Tarjetas de totales */}
                    <Row className="g-3 mb-3">
                        <Col xs={6} md={3}>
                            <Card className={cardClass + " text-center py-3"}>
                                <div className="informe-uif-stat text-primary">{resumen.totalAltasPorLegajo}</div>
                                <div className="informe-uif-stat-label">Clientes nuevos</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>altas por legajo único</div>
                            </Card>
                        </Col>
                        <Col xs={6} md={3}>
                            <Card className={cardClass + " text-center py-3"}>
                                <div className="informe-uif-stat text-danger">{resumen.totalBajasPorLegajo}</div>
                                <div className="informe-uif-stat-label">Clientes dados de baja</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>bajas por legajo único</div>
                            </Card>
                        </Col>
                        <Col xs={6} md={3}>
                            <Card className={cardClass + " text-center py-3"}>
                                <div className={`informe-uif-stat ${resumen.saldoNeto >= 0 ? "informe-uif-stat-neto-pos" : "informe-uif-stat-neto-neg"}`}>
                                    {resumen.saldoNeto >= 0 ? "+" : ""}{resumen.saldoNeto}
                                </div>
                                <div className="informe-uif-stat-label">Saldo neto</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>altas menos bajas</div>
                            </Card>
                        </Col>
                        <Col xs={6} md={3}>
                            <Card className={cardClass + " text-center py-3"}>
                                <div className="informe-uif-stat" style={{ fontSize: "1.3rem" }}>
                                    {resumen.altasPOS} POS / {resumen.altasEcommerce} EC
                                </div>
                                <div className="informe-uif-stat-label">Modalidad</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>POS vs Ecommerce</div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Nota registros sin legajo */}
                    {(resumen.altasSinLegajo > 0 || resumen.bajasSinLegajo > 0) && (
                        <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: "0.83rem" }}>
                            Excluidos del conteo: <strong>{resumen.altasSinLegajo}</strong> alta(s) y <strong>{resumen.bajasSinLegajo}</strong> baja(s) sin número de legajo asignado.
                        </div>
                    )}

                    {/* Distribuciones */}
                    <Row className="g-3 mb-3">
                        <Col xs={12} md={6}>
                            <Card className={cardClass}>
                                <Card.Body>
                                    <p className="informe-uif-section-title">Altas por provincia</p>
                                    <Table size="sm" bordered variant={tableVariant} className="informe-uif-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Provincia</th>
                                                <th className="text-end">Clientes</th>
                                                <th className="text-end">%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resumen.altasPorProvincia.map((d, i) => (
                                                <tr key={i}>
                                                    <td>{d.etiqueta}</td>
                                                    <td className="text-end">{d.cantidad}</td>
                                                    <td className="text-end">{d.porcentaje}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card className={cardClass}>
                                <Card.Body>
                                    <p className="informe-uif-section-title">Altas por rubro</p>
                                    <Table size="sm" bordered variant={tableVariant} className="informe-uif-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Rubro</th>
                                                <th className="text-end">Clientes</th>
                                                <th className="text-end">%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resumen.altasPorRubro.map((d, i) => (
                                                <tr key={i}>
                                                    <td>{d.etiqueta}</td>
                                                    <td className="text-end">{d.cantidad}</td>
                                                    <td className="text-end">{d.porcentaje}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Tabla altas */}
                    <Card className={cardClass + " mb-3"}>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <p className="informe-uif-section-title mb-0">
                                    Clientes nuevos — {resumen.altas.length} registros
                                </p>
                            </div>
                            <Table responsive size="sm" bordered hover variant={tableVariant} className="informe-uif-table">
                                <thead>
                                    <tr>
                                        <th>Legajo</th>
                                        <th>CUIT</th>
                                        <th>Nombre de Fantasía</th>
                                        <th>Titular</th>
                                        <th>Fecha Alta</th>
                                        <th>Rubro</th>
                                        <th>Provincia</th>
                                        <th>Modalidad</th>
                                        <th>Asesor</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginados(resumen.altas, paginaAltas).map((a, i) => (
                                        <tr key={i}>
                                            <td>{a.legajo ?? "—"}</td>
                                            <td>{a.cuit ?? "—"}</td>
                                            <td>{a.nombreFantasia ?? "—"}</td>
                                            <td>{a.titular ?? "—"}</td>
                                            <td>{formatFecha(a.fechaDeAlta)}</td>
                                            <td>{a.rubro ?? "—"}</td>
                                            <td>{a.provincia ?? "—"}</td>
                                            <td>{a.modalidadOperativa ?? "—"}</td>
                                            <td>{a.nombreAsesor ?? "—"}</td>
                                            <td>{a.estado ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {totalPaginas(resumen.altas) > 1 && (
                                <div className="d-flex gap-2 align-items-center justify-content-end mt-2">
                                    <Button size="sm" variant="outline-secondary" disabled={paginaAltas === 1} onClick={() => setPaginaAltas(p => p - 1)}>‹</Button>
                                    <span className="fs-14">{paginaAltas} / {totalPaginas(resumen.altas)}</span>
                                    <Button size="sm" variant="outline-secondary" disabled={paginaAltas === totalPaginas(resumen.altas)} onClick={() => setPaginaAltas(p => p + 1)}>›</Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Tabla bajas */}
                    <Card className={cardClass + " mb-3"}>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <p className="informe-uif-section-title mb-0">
                                    Clientes dados de baja — {resumen.bajas.length} registros
                                </p>
                            </div>
                            <Table responsive size="sm" bordered hover variant={tableVariant} className="informe-uif-table">
                                <thead>
                                    <tr>
                                        <th>Legajo</th>
                                        <th>CUIT</th>
                                        <th>Nombre de Fantasía</th>
                                        <th>Titular</th>
                                        <th>Fecha Alta</th>
                                        <th>Fecha Baja</th>
                                        <th>Tipo Baja</th>
                                        <th>Rubro</th>
                                        <th>Provincia</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginados(resumen.bajas, paginaBajas).map((b, i) => (
                                        <tr key={i}>
                                            <td>{b.legajo ?? "—"}</td>
                                            <td>{b.cuit ?? "—"}</td>
                                            <td>{b.nombreFantasia ?? "—"}</td>
                                            <td>{b.titular ?? "—"}</td>
                                            <td>{formatFecha(b.fechaDeAlta)}</td>
                                            <td>{formatFecha(b.fechaDeBaja)}</td>
                                            <td>{b.tipoDeBaja ?? "—"}</td>
                                            <td>{b.rubro ?? "—"}</td>
                                            <td>{b.provincia ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {totalPaginas(resumen.bajas) > 1 && (
                                <div className="d-flex gap-2 align-items-center justify-content-end mt-2">
                                    <Button size="sm" variant="outline-secondary" disabled={paginaBajas === 1} onClick={() => setPaginaBajas(p => p - 1)}>‹</Button>
                                    <span className="fs-14">{paginaBajas} / {totalPaginas(resumen.bajas)}</span>
                                    <Button size="sm" variant="outline-secondary" disabled={paginaBajas === totalPaginas(resumen.bajas)} onClick={() => setPaginaBajas(p => p + 1)}>›</Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Exportar */}
                    <Card className={cardClass}>
                        <Card.Body>
                            <p className="informe-uif-section-title">Exportar</p>
                            <Button
                                variant="success"
                                onClick={exportarExcel}
                                disabled={exportando}
                            >
                                {exportando
                                    ? <Spinner size="sm" animation="border" className="me-2" />
                                    : <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                                }
                                Exportar Excel completo
                            </Button>
                            <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.78rem" }}>
                                Incluye: Resumen, Altas del período, Bajas del período, Distribución geográfica, Distribución por rubro.
                            </p>
                        </Card.Body>
                    </Card>
                </>
            )}
        </div>
    );
};

export default PanelInformeUIF;
